import { Injectable } from '@angular/core';
import { CompFuncRoleCondition } from '@app/models/bd-role.model';
import { Staff } from '@app/models/staff.model';
import { User, UserByUnit } from '@app/models/user.model';
import { StaffInTeam, UserInGroup } from '@app/models/groupEntitiesData.model';
import { ApiService } from '@app/services/api.service';
import {
  Action,
  State,
  StateContext,
} from '@ngxs/store';
import { tap } from 'rxjs';
import { RecordList } from '@app/models/record.model';
import { Note } from '@app/models/note.model';
import * as moment from 'moment';
import { DaiDbService } from '@app/services/indexdb/dai-db.service';
import { NotelistDbService } from '@app/services/indexdb/notelist-db.service';
import { UserByUnitList } from '@app/models/profile.model';
import { createObjCd } from '@app/shared/common/utils';
import { 
  ClearUnsave, 
  RemoveDataSet, 
  RemoveUserImage, 
  SelectUser, SetAllRecords, 
  SetBDRoleList, SetCareConneRoomList, 
  SetPersonalFilter, SetRenderedDataNote, 
  SetStaffInTeamList, SetStaffList, SetUnsave, 
  SetUserImage, SetUserInGroupList, SetUserList 
} from './data.classes';

export interface Unsave {
  originalNote?: Note[];
  newNote: Note[];
  insdate: Date;
  location: string;

}

export interface DataStateModel {
  staffList: { [key: string]: Staff[] };
  userList: { [key: string]: User[] };
  userInGroupList: { [key: string]: UserInGroup[] };
  staffInTeamList: { [key: string]: StaffInTeam[] };
  userImage: { [key: string]: string };
  selectedUser: User | null;
  bdRoleList: CompFuncRoleCondition[];
  allRecords: RecordList[];
  unsave: Unsave[];
  // personal filter object
  personalSetting: {
    daicds: string[]
  }
  userByUnitList: UserByUnitList[];
  careConneRoomList: { 
    [key: string]: UserByUnit
  };
  renderedDataNote: any;
}

@State<DataStateModel>({
  name: 'dataState',
  defaults: {
    staffList: {},
    userList: {},
    userInGroupList: {},
    userImage: {},
    staffInTeamList: {},
    selectedUser: null,
    bdRoleList: [],
    allRecords: [],
    unsave: [],
    personalSetting: { daicds: [] },
    userByUnitList: [],
    careConneRoomList: {},
    renderedDataNote: {}
  },
})
@Injectable()
export class DataState {

  constructor(
    private apiService: ApiService,
    private daiIndexDbService: DaiDbService,
    private notesGroupIndexService: NotelistDbService
  ) { }

  @Action(SetStaffList)
  setStaffList(ctx: StateContext<DataStateModel>, { objectcd }: SetStaffList) {
    // const { staffList } = ctx.getState();

    // if (staffList[objectcd]) return;

    return this.apiService.getTeamStaffList().pipe(
      tap((res) => {
        const staffList = res.map((item: any) => {
          const name = `${item.family}${item.given}`;
          return { ...item, name };
        });
        ctx.patchState({ staffList: { [objectcd]: staffList } });
      })
    );
  }

  @Action(SetUnsave)
  setUnsave(ctx: StateContext<DataStateModel>, { data }: SetUnsave) {
    let { unsave } = ctx.getState();
    if (!unsave) {
      unsave = [];
      ctx.patchState({ unsave: [] });
    }
    const findlocationIndex = unsave.findIndex(x => x.location === data.location);
    if (findlocationIndex < 0) {
      ctx.patchState({ unsave: [data] });
    } else {
      ctx.patchState({ unsave: [...[...unsave].splice(findlocationIndex, 0), data] });
    }
  }

  @Action(ClearUnsave)
  clearUnsave(ctx: StateContext<DataStateModel>, { location }: ClearUnsave) {
    const { unsave } = ctx.getState();
    if (!location) {
      ctx.patchState({ unsave: [] });
      return
    }
    const findlocationIndex = unsave.findIndex(x => x.location === location);
    if (findlocationIndex < 0) return;
    ctx.patchState({ unsave: [...[...unsave].splice(findlocationIndex, 0)] });
  }

  @Action(SetUserList)
  setUserList(ctx: StateContext<DataStateModel>, { objectcd }: SetUserList) {
    const { selectedUser } = ctx.getState();
    const _objcd = objectcd || localStorage.getItem('objectcd') || '';
    if (ctx.getState().userList[_objcd]?.length > 0) return;
    return this.apiService.getUserList().pipe(
      tap((res) => {
        const units = this.extractUnits(res);
        const users = units.flatMap(x => x.items).map((item) => ({ ...item, name: `${item.family}${item.given}`, kana: `${item.kanafamily}${item.kanagiven}` }));
        const selectedUserInfo = users.find(x => x.daicd === selectedUser?.daicd);
        ctx.patchState({
          userList: { [objectcd]: users },
          selectedUser: selectedUserInfo ?? users[0],
          userByUnitList: units
        });
      })
    );
  }

  @Action(SetUserInGroupList)
  setUserInGroupList(ctx: StateContext<DataStateModel>, { objectcd }: SetUserInGroupList) {
    // const { userInGroupList } = ctx.getState();
    // if (userInGroupList[objectcd] && userInGroupList[objectcd]?.length > 0) return;
    return this.apiService.getUserInGroupList({ OwnUnitcd: objectcd }).pipe(
      tap((res) => {
        const userInGroupList = res && res.length > 0 ? [...res] : [];
        ctx.patchState({
          userInGroupList: { [objectcd]: userInGroupList },
        });
      })
    );
  }

  @Action(SetStaffInTeamList)
  setStaffInTeamList(ctx: StateContext<DataStateModel>, { objectcd }: SetStaffInTeamList) {
    // const { staffInTeamList } = ctx.getState();
    // if (staffInTeamList[objectcd] && staffInTeamList[objectcd]?.length > 0) return;
    return this.apiService.getStaffInTeamList({ OwnUnitcd: objectcd }).pipe(
      tap((res) => {
        const staffInTeam = res && res.length > 0 ? [...res] : [];
        ctx.patchState({
          staffInTeamList: { [objectcd]: staffInTeam },
        });
      })
    );
  }

  @Action(SetUserImage)
  async setUserImage(_: StateContext<DataStateModel>, { daicd }: SetUserImage) {
    return new Promise(async (resolve, _) => {
      // const { userImage } = ctx.getState();
      const isHavingAvtInState = await this.daiIndexDbService.getDaiInfo(daicd);
      const upddate = isHavingAvtInState?.upddate || null;
      const getImage = await this.apiService.getUserImage(daicd, upddate || undefined);
      getImage.subscribe(async (res) => {
        if (!res?.bldata) { resolve(false); return; }
        const uppdateFormat = 'YYYY-MM-DD HH:mm:ss';
        const blbData = await this.resizeBase64Image(res?.bldata);
        //.then((blbData: string) => {
        // if (!blbData || blbData == '') return;
        // const bldata = [blbData ?? '-1', res.upddate ?? moment().format(uppdateFormat)].join(joinStringCharater);
        // const { userImage } = ctx.getState();
        // ctx.patchState({ userImage: { ...userImage, [daicd]: bldata } });

        //})
        this.daiIndexDbService.setDaiImage({
          daicd, image: blbData || '', upddate: res.upddate ?? moment().format(uppdateFormat)
        }).finally(() => {
          resolve(true);
        })
      })
    })
  }

  private resizeBase64Image = (bldata: string): Promise<string> => {
    if (bldata === undefined || bldata === '') return new Promise((resolve, _) => { resolve('') });
    return new Promise((resolve, _) => {
      const maxSizeInMB = 0.006;
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
      const img = new Image();
      img.src = `data:image/png;base64,${bldata}`;
      img.onload = function () {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext('2d');
        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;
        const newWidth = Math.sqrt(maxSizeInBytes * aspectRatio);
        const newHeight = Math.sqrt(maxSizeInBytes / aspectRatio);
        canvas.width = newWidth;
        canvas.height = newHeight;
        if (ctx) {
          ctx.drawImage(img, 0, 0, newWidth, newHeight);
          let quality = 0.8;
          let dataURL = canvas.toDataURL('image/png', quality);
          resolve(dataURL.replace('data:image/png;base64,', ''));
        }
        resolve(bldata);
      };
    });
  }

  @Action(RemoveUserImage)
  removeUserImage(ctx: StateContext<DataStateModel>) {
    ctx.patchState({ userImage: {} });
  }


  @Action(SetBDRoleList)
  setBDRoleList(ctx: StateContext<DataStateModel>) {
    return this.apiService.checkRoleByStaff().pipe(
      tap((res) =>
        ctx.patchState({ bdRoleList: res })
      )
    );
  }

  @Action(SelectUser)
  selectUser(ctx: StateContext<DataStateModel>, { selectedUser }: SelectUser) {
    ctx.patchState({ selectedUser });
  }

  @Action(SetPersonalFilter)
  selectPersonal(ctx: StateContext<DataStateModel>, { daicds }: SetPersonalFilter) {
    ctx.patchState({ personalSetting: { daicds: daicds } });
  }

  @Action(RemoveDataSet)
  remove(ctx: StateContext<DataStateModel>) {
    this.daiIndexDbService.removeIndexDb();
    ctx.patchState({
      staffList: {},
      userList: {},
      userImage: {},
      personalSetting: { daicds: [] },
      selectedUser: null,
      bdRoleList: [],
      allRecords: [],
      unsave: []
    });
  }

  @Action(SetAllRecords)
  setAllRecords(
    ctx: StateContext<DataStateModel>,
    { payload, groupId }: SetAllRecords
  ) {
    ctx.patchState({ allRecords: payload });
    if (groupId) {
      this.notesGroupIndexService.addNotesByGroup({ groupId, list: payload })
    }
  }

  private extractUnits(data: any): any[] {
    const units: any[] = [];

    const traverse = (
      nodes: any[],
      office?: any,
      block?: any
    ) => {
      nodes?.forEach(node => {

        if (node.level === 0) {
          traverse(node.children, node, undefined);
          return;
        }

        if (node.level === 1) {
          traverse(node.children, office, node);
          return;
        }

        if (node.level === 2 && node.items) {
          const firstItem = node.items[0];
          units.push({
            ...node,
            officecd: office?.code,
            officeName: office?.name,
            blockcd: block?.code,
            blockName: block?.name,
            objcd: createObjCd(firstItem.corpcd, firstItem.officecd ,firstItem.blockcd, firstItem.unitcd)
          });
        }

        if (node.children) {
          traverse(node.children, office, block);
        }
      });
    };

    traverse(data.roots);
    return units;
  }

  @Action(SetCareConneRoomList)
  setCareConneRoomList(
    ctx: StateContext<DataStateModel>, 
    { roomListByUser }: SetCareConneRoomList
  ) {
    ctx.patchState({ careConneRoomList: { ...roomListByUser } });
  }

  @Action(SetRenderedDataNote)
  setRenderedDataNote(
    ctx: StateContext<DataStateModel>,
    { renderedDataNote }: SetRenderedDataNote
  ) {
    ctx.patchState({ renderedDataNote });
  }
}

export enum DATA_STATE_LOCAL_KEY {
  DATA_STATE = "dataState"
}
